/**
 * useAutoRefresh — silent background refresh for the dashboards.
 *
 * Keeps dashboard data fresh without websockets or Supabase Realtime,
 * using two simple mechanisms:
 *
 *   1. Refetch when the browser tab regains focus/visibility
 *      (the `visibilitychange` event firing with state 'visible').
 *   2. Background polling on an interval (default 45 seconds) that
 *      runs ONLY while the tab is visible — the interval is cleared
 *      when the tab is hidden and restarted when it becomes visible.
 *
 * The callback passed in should be the dashboard's EXISTING data-fetch
 * function (called in "silent" mode so it doesn't flip the loading
 * skeleton back on). This hook never duplicates fetch logic — it just
 * re-runs what the dashboard already does on mount.
 *
 * Safety guarantees:
 *   - Overlap guard: if a refetch is still in flight, the next tick is
 *     skipped rather than stacking a second request on top.
 *   - Errors fail silently to the console (console.warn) — a background
 *     refresh must never surface an error popup; the user simply keeps
 *     seeing the last good data.
 *   - All listeners and intervals are cleaned up on unmount.
 */

import { useEffect, useRef } from 'react';

export default function useAutoRefresh(refetch, intervalMs = 45000) {
  // Keep the latest callback in a ref so the effect below never needs to
  // re-run (and reset its interval) just because the caller re-rendered.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  // True while a background refetch is in flight — used to skip ticks
  // rather than stacking overlapping requests.
  const inFlightRef = useRef(false);

  useEffect(() => {
    let intervalId = null;

    const tick = async () => {
      if (inFlightRef.current) return; // previous refetch still running — skip
      inFlightRef.current = true;
      try {
        await refetchRef.current();
      } catch (err) {
        // Background refreshes fail silently — console only, never a popup.
        console.warn('[auto-refresh] background refresh failed:', err);
      } finally {
        inFlightRef.current = false;
      }
    };

    const startPolling = () => {
      if (intervalId === null) {
        intervalId = setInterval(tick, intervalMs);
      }
    };

    const stopPolling = () => {
      if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Tab regained focus/visibility — refresh immediately, then
        // resume polling.
        tick();
        startPolling();
      } else {
        // Tab hidden — pause polling entirely.
        stopPolling();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    // Start polling right away if the tab is already visible (the normal
    // case on mount). If it mounts hidden, polling starts on the first
    // visibilitychange back to visible.
    if (document.visibilityState === 'visible') {
      startPolling();
    }

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      stopPolling();
    };
  }, [intervalMs]);
}
